package org.gymly.ui.screens

import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.BoxWithConstraints
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.AlertDialog
import androidx.compose.material3.Button
import androidx.compose.material3.ButtonDefaults
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.pulltorefresh.PullToRefreshBox
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import kotlinx.coroutines.launch
import org.gymly.data.PortalRepo
import org.gymly.esAndroid
import org.gymly.model.PortalBooking
import org.gymly.model.PortalBookingStatus
import org.gymly.theme.GymlyColors
import org.gymly.theme.GymlySpace
import org.gymly.ui.components.GymlyLoadingCenter
import org.gymly.ui.components.MobilePageHeader
import org.gymly.util.formatPortalSessionLabel
import org.gymly.util.userMessage

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun BookingsScreen(
    mobile: Boolean = false,
    unreadCount: Long = 0,
    langEs: Boolean = true,
    onLangEs: (Boolean) -> Unit = {},
    onBell: () -> Unit = {},
) {
    val scope = rememberCoroutineScope()
    var rows by remember { mutableStateOf<List<PortalBooking>>(emptyList()) }
    var loading by remember { mutableStateOf(true) }
    var refreshing by remember { mutableStateOf(false) }
    var error by remember { mutableStateOf<String?>(null) }
    var cancellingId by remember { mutableStateOf<String?>(null) }
    var confirmCancel by remember { mutableStateOf<PortalBooking?>(null) }

    fun load() {
        scope.launch {
            error = null
            try {
                rows = PortalRepo.api.fetchBookings()
            } catch (e: Exception) {
                error = e.userMessage("Error al cargar reservas.")
            } finally {
                loading = false
                refreshing = false
            }
        }
    }

    fun cancel(item: PortalBooking) {
        scope.launch {
            cancellingId = item.id
            error = null
            try {
                PortalRepo.api.cancelBooking(item.id)
                confirmCancel = null
                load()
            } catch (e: Exception) {
                error = e.userMessage("No se pudo cancelar.")
            } finally {
                cancellingId = null
            }
        }
    }

    LaunchedEffect(Unit) { load() }

    if (loading && rows.isEmpty()) {
        GymlyLoadingCenter()
        return
    }

    val subtitle = "Tus reservas de clases próximas y pasadas."

    BoxWithConstraints(Modifier.fillMaxSize().background(GymlyColors.Surface)) {
        val compact = mobile || maxWidth < 700.dp
        val contenido: @Composable () -> Unit = {
            Column(Modifier.fillMaxSize()) {
                if (mobile) {
                    MobilePageHeader(
                        title = "Reservas",
                        subtitle = subtitle,
                        unreadCount = unreadCount,
                        langEs = langEs,
                        onLangEs = onLangEs,
                        onBell = onBell,
                    )
                }

                Column(
                    Modifier
                        .fillMaxSize()
                        .verticalScroll(rememberScrollState())
                        .padding(GymlySpace.lg),
                ) {
                    if (!mobile) {
                        Text("Reservas", fontWeight = FontWeight.Bold, fontSize = 28.sp, color = GymlyColors.Text)
                        Spacer(Modifier.height(6.dp))
                        Text(subtitle, color = GymlyColors.TextMuted, fontSize = 14.sp)
                        Spacer(Modifier.height(16.dp))
                    }
                    error?.let {
                        Text(it, color = GymlyColors.Error, modifier = Modifier.padding(bottom = 10.dp))
                    }
                    if (rows.isEmpty()) {
                        Text(
                            "Aún no tienes reservas. Reserva desde Horarios.",
                            color = GymlyColors.TextMuted,
                        )
                    } else {
                        rows.forEach { item ->
                            ReservationCard(
                                item = item,
                                compact = compact,
                                cancelling = cancellingId == item.id,
                                onCancel = { confirmCancel = item },
                            )
                            Spacer(Modifier.height(12.dp))
                        }
                    }
                    Spacer(Modifier.height(GymlySpace.xl))
                }
            }
        }
        // Solo la app de Android tiene el gesto de jalar para actualizar; la web se queda como estaba.
        if (esAndroid()) {
            PullToRefreshBox(isRefreshing = refreshing, onRefresh = { refreshing = true; load() }, modifier = Modifier.fillMaxSize()) {
                contenido()
            }
        } else {
            contenido()
        }
    }

    confirmCancel?.let { item ->
        AlertDialog(
            onDismissRequest = { if (cancellingId == null) confirmCancel = null },
            title = { Text("Cancelar reserva") },
            text = { Text("¿Cancelar «${item.title}» del ${item.sessionDate}? Esta acción no se puede deshacer.") },
            confirmButton = {
                Button(
                    onClick = { cancel(item) },
                    enabled = cancellingId == null,
                    colors = ButtonDefaults.buttonColors(containerColor = GymlyColors.Error),
                ) {
                    if (cancellingId == item.id) {
                        CircularProgressIndicator(Modifier.padding(4.dp), color = Color.White, strokeWidth = 2.dp)
                    } else {
                        Text("Cancelar reserva")
                    }
                }
            },
            dismissButton = {
                TextButton(onClick = { confirmCancel = null }, enabled = cancellingId == null) {
                    Text("Volver")
                }
            },
        )
    }
}

@Composable
private fun ReservationCard(
    item: PortalBooking,
    compact: Boolean,
    cancelling: Boolean,
    onCancel: () -> Unit,
) {
    val whenLabel = formatPortalSessionLabel(item.sessionDate, item.weekday, item.startTime, item.endTime)
    Column(
        Modifier
            .fillMaxWidth()
            .clip(RoundedCornerShape(14.dp))
            .background(GymlyColors.SurfaceContainer)
            .border(1.dp, GymlyColors.Outline.copy(alpha = 0.55f), RoundedCornerShape(14.dp))
            .padding(horizontal = 18.dp, vertical = 16.dp),
    ) {
        if (compact) {
            Text(item.title, fontWeight = FontWeight.Bold, fontSize = 17.sp, color = GymlyColors.Text)
            Spacer(Modifier.height(4.dp))
            Text(whenLabel, color = GymlyColors.TextMuted, fontSize = 13.sp)
            Spacer(Modifier.height(10.dp))
            Row(
                Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.SpaceBetween,
                verticalAlignment = Alignment.CenterVertically,
            ) {
                BookingStatusBadge(item)
                if (item.canCancel) {
                    CancelLink(cancelling = cancelling, onCancel = onCancel)
                }
            }
        } else {
            Row(
                Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.SpaceBetween,
                verticalAlignment = Alignment.CenterVertically,
            ) {
                Column(Modifier.weight(1f).padding(end = 12.dp)) {
                    Text(item.title, fontWeight = FontWeight.Bold, fontSize = 17.sp, color = GymlyColors.Text)
                    Spacer(Modifier.height(4.dp))
                    Text(whenLabel, color = GymlyColors.TextMuted, fontSize = 13.sp)
                }
                Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(16.dp)) {
                    BookingStatusBadge(item)
                    if (item.canCancel) {
                        CancelLink(cancelling = cancelling, onCancel = onCancel)
                    } else {
                        Spacer(Modifier.width(4.dp))
                    }
                }
            }
        }
    }
}

@Composable
private fun BookingStatusBadge(item: PortalBooking) {
    val (fg, bg) = when (item.status) {
        PortalBookingStatus.CONFIRMED -> Color(0xFF3B4CB8) to Color(0xFFE8EEFF)
        PortalBookingStatus.ATTENDED -> Color(0xFF2E7D32) to Color(0xFFE8F5E9)
        PortalBookingStatus.CANCELLED, PortalBookingStatus.NO_SHOW ->
            Color(0xFFC62828) to Color(0xFFFFEBEE)
    }
    Text(
        item.statusLabel,
        color = fg,
        fontSize = 12.sp,
        fontWeight = FontWeight.SemiBold,
        modifier = Modifier
            .clip(RoundedCornerShape(8.dp))
            .background(bg)
            .padding(horizontal = 10.dp, vertical = 5.dp),
    )
}

@Composable
private fun CancelLink(cancelling: Boolean, onCancel: () -> Unit) {
    if (cancelling) {
        CircularProgressIndicator(
            Modifier.padding(4.dp),
            color = GymlyColors.Error,
            strokeWidth = 2.dp,
        )
    } else {
        Text(
            "Cancelar reserva",
            color = GymlyColors.Error,
            fontWeight = FontWeight.SemiBold,
            fontSize = 14.sp,
            modifier = Modifier.clickable(onClick = onCancel),
        )
    }
}
